import { useMemo, useState } from 'react';
import type { Behavior, FieldContent } from '@capture/shared';
import { useFormConfigStore } from '../state/store';
import './HeaderFieldComposer.css';

interface Props {
  format: 'xml' | 'json';
  path: string;
  onAdded: () => void;
  onCancel: () => void;
}

/** Snake_case the last meaningful segment of an XPath / JSONPath. */
function suggestNameFromPath(path: string): string {
  const trimmed = path
    .replace(/\/text\(\)\s*$/, '')
    .replace(/^[\$@\/]+/, '')
    .replace(/^\.+/, '');
  const segments = trimmed.split(/[\/.]/).filter((s) => s.length > 0);
  const last = segments[segments.length - 1] ?? '';
  // CamelCase / PascalCase → snake_case
  return last
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^\w]+/g, '_')
    .toLowerCase();
}

const DATA_TYPES: FieldContent['dataType'][] = [
  'text', 'number', 'currency', 'date', 'boolean', 'decimal',
];

export function HeaderFieldComposer({ format, path, onAdded, onCancel }: Props) {
  const addBatchField = useFormConfigStore((s) => s.addBatchField);
  const addBehavior = useFormConfigStore((s) => s.addBehavior);
  const existingFields = useFormConfigStore((s) => s.content?.fields ?? []);

  const [name, setName] = useState(() => suggestNameFromPath(path));
  const [dataType, setDataType] = useState<FieldContent['dataType']>('text');
  const [required, setRequired] = useState(false);

  const nameError = useMemo(() => {
    if (name.trim() === '') return 'Name is required';
    if (!/^[a-z][a-z0-9_]*$/.test(name)) return 'Use snake_case letters / digits / underscores';
    if (existingFields.some((f) => f.name === name)) return `Field "${name}" already exists`;
    return null;
  }, [name, existingFields]);

  function handleAdd() {
    if (nameError) return;
    // addBatchField initializes stageMap with pre-export and export behaviors.
    // Use addBehavior to append the import behavior so those stages are preserved.
    addBatchField(name, dataType);
    const source =
      format === 'xml'
        ? ({ type: 'header-xpath', xpath: path, required } as const)
        : ({ type: 'header-path', path, required } as const);
    const populate: Behavior = { kind: 'populate', source };
    addBehavior(name, 'import', populate);
    onAdded();
  }

  return (
    <div className="header-field-composer" role="dialog" aria-label="New header field">
      <h4 className="header-field-composer__title">New header field</h4>
      <label className="header-field-composer__field">
        <span>Name</span>
        <input
          className="header-field-composer__input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-invalid={nameError !== null}
        />
        {nameError && (
          <span className="header-field-composer__error">{nameError}</span>
        )}
      </label>
      <label className="header-field-composer__field">
        <span>Type</span>
        <select
          className="header-field-composer__select"
          value={dataType}
          onChange={(e) => setDataType(e.target.value as FieldContent['dataType'])}
        >
          {DATA_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </label>
      <label className="header-field-composer__field">
        <span>Path</span>
        <input
          className="header-field-composer__input header-field-composer__input--readonly"
          value={path}
          readOnly
        />
      </label>
      <label className="header-field-composer__field header-field-composer__field--inline">
        <input
          type="checkbox"
          checked={required}
          onChange={(e) => setRequired(e.target.checked)}
        />
        <span>Required</span>
      </label>
      <div className="header-field-composer__actions">
        <button
          type="button"
          className="header-field-composer__btn header-field-composer__btn--primary"
          onClick={handleAdd}
          disabled={nameError !== null}
        >
          Add
        </button>
        <button
          type="button"
          className="header-field-composer__btn"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
